import React from 'react';
import './TermsAndConditions.scss';

const TermsAndConditions = () => (
  <main className="legal-page" data-testid="terms-page">
    <div className="legal-page__container">
      <h1 className="legal-page__title">Terms and Conditions</h1>
      <p className="legal-page__effective">Effective Date: April 3, 2026</p>

      <section className="legal-page__section">
        <p>
          Welcome to HaulSync. By accessing or using the HaulSync platform — including our website
          and mobile application (collectively, the "Service") — you agree to be bound by these
          Terms and Conditions ("Terms"). Please read them carefully before using the Service.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">1. About HaulSync</h2>
        <p>
          HaulSync is Ontario's digital marketplace for heavy hauling. We connect WSIB-verified
          carriers with Ontario-based contractors through a transparent platform that charges a
          flat 5% service fee. HaulSync is operated by Appcastlehub.com and currently serves
          the Greater Toronto Area (GTA).
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">2. Eligibility</h2>
        <p>
          You must be at least 18 years of age and legally authorized to operate a business in
          Ontario, Canada to register and use this Service. By creating an account, you represent
          and warrant that you meet these requirements.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">3. User Accounts</h2>
        <p>
          HaulSync offers two account types: <strong>Contractors</strong> (construction companies
          seeking hauling services) and <strong>Carriers</strong> (truck operators providing
          hauling services). You are responsible for maintaining the confidentiality of your
          account credentials and for all activities that occur under your account.
        </p>
        <p>
          You agree to provide accurate, complete, and up-to-date information when registering
          and to promptly update your account if any information changes.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">4. WSIB Verification</h2>
        <p>
          Carriers on HaulSync are required to be verified under the Workplace Safety and
          Insurance Board (WSIB) of Ontario. By registering as a Carrier, you consent to
          HaulSync verifying your WSIB clearance certificate. Providing false WSIB information
          is grounds for immediate account suspension and may result in legal action.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">5. Platform Fee</h2>
        <p>
          HaulSync charges a flat service fee of <strong>5%</strong> of the total job value for
          each completed haul facilitated through the platform. This fee is automatically
          deducted from the payment processed through the Service. HaulSync reserves the right
          to modify its fee structure with reasonable notice to users.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">6. Bookings and Payments</h2>
        <p>
          All job bookings, confirmations, and payments must be processed through the HaulSync
          platform. Users are prohibited from circumventing the platform to avoid service fees.
          HaulSync is not responsible for disputes arising from transactions made outside the
          platform.
        </p>
        <p>
          Payment processing is handled by our third-party payment providers. By using the
          Service, you agree to the terms of those payment processors.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">7. Prohibited Conduct</h2>
        <p>You agree not to:</p>
        <ul className="legal-page__list">
          <li>Use the Service for any unlawful purpose or in violation of any applicable laws.</li>
          <li>Provide false or misleading information about yourself, your business, or your equipment.</li>
          <li>Harass, threaten, or discriminate against other users.</li>
          <li>Attempt to gain unauthorized access to any part of the Service or another user's account.</li>
          <li>Circumvent or interfere with the platform's fee structure or payment system.</li>
          <li>Scrape, copy, or reproduce any part of the Service without prior written consent.</li>
        </ul>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">8. Cancellations and Disputes</h2>
        <p>
          Cancellation policies are governed by the terms agreed upon at the time of booking.
          In the event of a dispute between a Contractor and a Carrier, HaulSync may, at its
          discretion, assist in mediation but is not obligated to resolve disputes. HaulSync's
          decision in any dispute is final and binding.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">9. Intellectual Property</h2>
        <p>
          All content, branding, logos, software, and features of the HaulSync platform are the
          exclusive property of HaulSync and its licensors. You may not reproduce, distribute,
          or create derivative works without express written permission.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">10. Disclaimers</h2>
        <p>
          HaulSync is a marketplace platform only. We do not employ carriers, operate trucks, or
          perform hauling services directly. HaulSync makes no warranties regarding the quality,
          safety, legality, or availability of any services listed on the platform. The Service
          is provided "as is" and "as available" without warranty of any kind.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">11. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by applicable law, HaulSync and its affiliates,
          officers, employees, and agents shall not be liable for any indirect, incidental,
          special, consequential, or punitive damages arising from your use of the Service,
          including but not limited to property damage, personal injury, loss of revenue, or
          data loss.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">12. Termination</h2>
        <p>
          HaulSync reserves the right to suspend or terminate your account at any time, with or
          without notice, for violations of these Terms or for any other reason at our sole
          discretion. You may terminate your account at any time by contacting us.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">13. Governing Law</h2>
        <p>
          These Terms are governed by and construed in accordance with the laws of the Province
          of Ontario and the federal laws of Canada applicable therein. Any disputes shall be
          resolved exclusively in the courts of Ontario.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">14. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. We will notify you of material changes by
          posting the updated Terms on this page and updating the effective date. Continued use
          of the Service after changes constitutes your acceptance of the revised Terms.
        </p>
      </section>

      <section className="legal-page__section">
        <h2 className="legal-page__section-title">15. Contact Us</h2>
        <p>
          If you have any questions about these Terms, please contact us at:
        </p>
        <address className="legal-page__address">
          <strong>HaulSync</strong><br />
          Ontario, Canada<br />
          Email: <a href="mailto:support@haulsync.ca">support@haulsync.ca</a>
        </address>
      </section>
    </div>
  </main>
);

export default TermsAndConditions;
